import "./sentry"; // Initialize Sentry first so it captures errors from app startup onward.
import * as Sentry from "@sentry/react";
import { installDomResilience } from "./domResilience";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import App from "./App";
import Login from "./components/Login";
import Registration from "./components/Registration";
import Checkout from "./components/Checkout";
import CourtCheckout from "./components/CourtCheckout";
import CoachingCheckout from "./components/CoachingCheckout";
import ActivityCheckout from "./components/ActivityCheckout";
import AddOnPage from "./components/AddOnPage";
import MembershipUpgrade from "./components/MembershipUpgradeForm";
import ProfilePage from "./components/ProfilePage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import { supabase } from "./supabase";
import { initGA4, trackPageView } from "./analytics";

// Initialize Google Analytics 4 as early as possible.
initGA4();

// Fires a GA4 page_view on every React Router navigation. Without this, an SPA
// only reports the initial page load.
const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
};

// ProtectedRoute component to handle authentication checks
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = React.useState(null); // Track auth state

  useEffect(() => {
    // Initial session check
    const checkSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        console.error("Error checking session:", error);
        setIsAuthenticated(false);
        navigate("/login", {
          state: { from: location.pathname, ...location.state },
        });
        return;
      }
      if (!session) {
        // //console.log('No active session found, redirecting to login.');
        setIsAuthenticated(false);
        navigate("/login", {
          state: { from: location.pathname, ...location.state },
        });
      } else {
        setIsAuthenticated(true);
      }
    };

    checkSession();

    // Listen for auth state changes (e.g., sign-out)
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_OUT") {
          // //console.log('User signed out, redirecting to login.');
          setIsAuthenticated(false);
          navigate("/login", {
            state: { from: location.pathname, ...location.state },
            replace: true,
          });
        } else if (event === "SIGNED_IN" && session) {
          setIsAuthenticated(true);
        }
      }
    );

    // Cleanup listener on unmount
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate, location]);

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="loading-spinner-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Don't render children if not authenticated (redirect already handled)
  if (!isAuthenticated) {
    return null;
  }

  return children;
};

// Route-aware wrapper so navigation / page-load performance transactions are
// named by route (used by the React Router v7 tracing integration in ./sentry).
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// Fallback shown if a render error escapes all the way to the root, instead of a
// blank white screen. The error itself is already captured by Sentry.
const RootErrorFallback = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "1rem",
      padding: "2rem",
      textAlign: "center",
      fontFamily: "system-ui, sans-serif",
    }}
  >
    <h1 style={{ margin: 0 }}>Something went wrong</h1>
    <p style={{ margin: 0, color: "#555" }}>
      Sorry about that — please reload the page to continue.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: "0.6rem 1.2rem",
        borderRadius: "8px",
        border: "none",
        background: "#111",
        color: "#fff",
        cursor: "pointer",
        fontSize: "1rem",
      }}
    >
      Reload
    </button>
  </div>
);

// Tolerate third-party DOM mutation (Google Translate, in-app browsers, extensions)
// instead of letting it unmount the whole app. MUST run before the first render.
installDomResilience();

const root = ReactDOM.createRoot(document.getElementById("root"), {
  // Forward React 19 render-time errors to Sentry (these are no-ops when Sentry
  // is disabled, e.g. local dev without a DSN).
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
      <BrowserRouter>
        <AnalyticsTracker />
        <SentryRoutes>
        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <App />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout-court"
          element={
            <ProtectedRoute>
              <CourtCheckout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout-coaching"
          element={
            <ProtectedRoute>
              <CoachingCheckout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout-activity"
          element={
            <ProtectedRoute>
              <ActivityCheckout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-ons"
          element={
            <ProtectedRoute>
              <AddOnPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/membership-upgrade"
          element={
            <ProtectedRoute>
              <MembershipUpgrade />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage onBack={() => window.history.back()} />
            </ProtectedRoute>
          }
        />

        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/registration" element={<Registration />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Catch-all Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </SentryRoutes>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
